import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import ContractListPlayerRow from './components/ContractListPlayerRow';
import LeagueNavbar from '../shared/LeagueNavbar';
import PlayerListFilters, {
  ALL,
  playerMatchesPosition,
} from '../shared/PlayerListFilters';
import './PendingFAList.css';

const isForward = (pos) => {
  const value = String(pos || '').toUpperCase();
  return value.includes('C') || value.includes('LW') || value.includes('RW') || value.includes('F');
};

const isDefense = (pos) =>
  String(pos || '').toUpperCase().includes('D') && !String(pos || '').toUpperCase().includes('G');

const isGoalie = (pos) => String(pos || '').toUpperCase().includes('G');

const playerMatchesPositionGroup = (player, check) =>
  (player.pos || []).some((pos) => check(pos));

const marketValue = (player) => {
  if (player.auction_value == null) return null;
  const value = Number(player.auction_value);
  return Number.isFinite(value) ? value : null;
};

const seasonPath = (nav) => {
  if (!nav) return null;
  const base = `/league/${nav.league_key}/pending_fa`;
  return nav.ahead > 0 ? `${base}?ahead=${nav.ahead}` : base;
};

const PendingFAList = () => {
  const { league_key } = useParams();
  const [searchParams] = useSearchParams();
  const ahead = Math.max(0, Number(searchParams.get('ahead') || 0) || 0);

  const [data, setData] = useState(null);
  const [positionFilter, setPositionFilter] = useState(ALL);
  const [teamFilter, setTeamFilter] = useState(ALL);
  const [sortKey, setSortKey] = useState('salary');
  const [sortDir, setSortDir] = useState('desc');
  const [summarySortKey, setSummarySortKey] = useState('salary');
  const [summarySortDir, setSummarySortDir] = useState('desc');

  useEffect(() => {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/league/${league_key}/pending_fa?ahead=${ahead}`;
    fetch(apiUrl)
      .then((response) => response.json())
      .then((payload) => {
        setData(payload);
        setTeamFilter(ALL);
        console.log(payload);
      });
  }, [league_key, ahead]);

  const teamByKey = useMemo(() => {
    const map = {};
    (data?.teams || []).forEach((team) => {
      map[team.team_key] = team;
    });
    return map;
  }, [data?.teams]);

  const teams = useMemo(() => {
    return [...(data?.teams || [])].sort((a, b) =>
      a.team_name.localeCompare(b.team_name)
    );
  }, [data?.teams]);

  const positionFilteredPlayers = useMemo(() => {
    return (data?.contract_list || []).filter((player) =>
      playerMatchesPosition(player, positionFilter)
    );
  }, [data?.contract_list, positionFilter]);

  const teamSummary = useMemo(() => {
    const statsByTeam = {};

    teams.forEach((team) => {
      const availableCap = Number(team.team_cap_summary?.available_cap) || 0;
      const projectedAvailableCap = team.projected_available_cap == null
        ? null
        : Number(team.projected_available_cap);

      statsByTeam[team.team_key] = {
        team_key: team.team_key,
        team_name: team.team_name,
        team_logo_url: team.team_logo_url,
        players: 0,
        salary: 0,
        totalExpiringSalary: 0,
        availableCap,
        expectedRemainingCap: projectedAvailableCap != null && Number.isFinite(projectedAvailableCap)
          ? projectedAvailableCap
          : availableCap,
        forwards: 0,
        defense: 0,
        goalies: 0,
      };
    });

    (data?.contract_list || []).forEach((player) => {
      const row = statsByTeam[player.team_key];
      if (!row) return;

      const salary = Number(player.salary) || 0;
      row.totalExpiringSalary += salary;
      if (teamByKey[player.team_key]?.projected_available_cap == null) {
        row.expectedRemainingCap = row.availableCap + row.totalExpiringSalary;
      }
    });

    positionFilteredPlayers.forEach((player) => {
      const row = statsByTeam[player.team_key];
      if (!row) return;

      const salary = Number(player.salary) || 0;
      row.players += 1;
      row.salary += salary;

      if (playerMatchesPositionGroup(player, isGoalie)) {
        row.goalies += 1;
      } else if (playerMatchesPositionGroup(player, isDefense)) {
        row.defense += 1;
      } else if (playerMatchesPositionGroup(player, isForward)) {
        row.forwards += 1;
      }
    });

    const rows = Object.values(statsByTeam);
    const direction = summarySortDir === 'asc' ? 1 : -1;

    rows.sort((a, b) => {
      if (summarySortKey === 'team') {
        return a.team_name.localeCompare(b.team_name) * direction;
      }
      return ((Number(a[summarySortKey]) || 0) - (Number(b[summarySortKey]) || 0)) * direction;
    });

    return rows;
  }, [teams, data?.contract_list, positionFilteredPlayers, summarySortKey, summarySortDir, teamByKey]);

  const displayedPlayers = useMemo(() => {
    let players = [...positionFilteredPlayers];

    if (teamFilter !== ALL) {
      players = players.filter((player) => player.team_key === teamFilter);
    }

    const direction = sortDir === 'asc' ? 1 : -1;
    players.sort((a, b) => {
      if (sortKey === 'salary') {
        return (Number(a.salary) - Number(b.salary)) * direction;
      }
      if (sortKey === 'market_value') {
        const marketA = marketValue(a);
        const marketB = marketValue(b);
        if (marketA == null) return marketB == null ? 0 : 1;
        if (marketB == null) return -1;
        return (Math.round(marketA) - Math.round(marketB)) * direction;
      }
      if (sortKey === 'team') {
        const nameA = teamByKey[a.team_key]?.team_name || '';
        const nameB = teamByKey[b.team_key]?.team_name || '';
        return nameA.localeCompare(nameB) * direction;
      }
      return 0;
    });

    return players;
  }, [positionFilteredPlayers, teamFilter, sortKey, sortDir, teamByKey]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'team' ? 'asc' : 'desc');
  };

  const toggleSummarySort = (key) => {
    if (summarySortKey === key) {
      setSummarySortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSummarySortKey(key);
    setSummarySortDir(key === 'team' ? 'asc' : 'desc');
  };

  const sortIndicator = (activeKey, key, dir) => {
    if (activeKey !== key) return '';
    return dir === 'asc' ? ' ▲' : ' ▼';
  };

  const handleTeamRowClick = (teamKey) => {
    setTeamFilter((prev) => (prev === teamKey ? ALL : teamKey));
  };

  const previousPath = seasonPath(data?.previous);
  const nextPath = seasonPath(data?.next);
  const seasonLabel = data?.season?.nhl_season || league_key;
  const isProjection = Boolean(data?.season?.is_projection);

  return (
    <div>
      {data && <LeagueNavbar teams={data.teams} league_key={league_key} />}

      <div className="page-content">
        <div className="pending-fa-season-nav">
          {previousPath ? (
            <Link to={previousPath} className="pending-fa-season-btn">
              ← {data.previous.nhl_season || 'Previous'}
            </Link>
          ) : (
            <span className="pending-fa-season-btn is-disabled">←</span>
          )}

          <div className="pending-fa-season-current">
            <span className="pending-fa-season-label">{seasonLabel}</span>
            {isProjection && <span className="pending-fa-season-badge">Projected</span>}
          </div>

          {nextPath ? (
            <Link to={nextPath} className="pending-fa-season-btn">
              {data.next.nhl_season || 'Next'} →
            </Link>
          ) : (
            <span className="pending-fa-season-btn is-disabled">→</span>
          )}
        </div>

        <PlayerListFilters
          teams={data?.teams || []}
          positionFilter={positionFilter}
          onPositionChange={setPositionFilter}
          teamFilter={teamFilter}
          onTeamChange={setTeamFilter}
        />

        <div className="pending-fa-summary">
          <Table responsive bordered hover className="table">
            <thead>
              <tr>
                <th className="name-th">
                  <button
                    type="button"
                    className="sortable-th"
                    onClick={() => toggleSummarySort('team')}
                  >
                    Team{sortIndicator(summarySortKey, 'team', summarySortDir)}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="sortable-th"
                    onClick={() => toggleSummarySort('players')}
                  >
                    Expiring Players{sortIndicator(summarySortKey, 'players', summarySortDir)}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="sortable-th"
                    onClick={() => toggleSummarySort('salary')}
                  >
                    Expiring Cap{sortIndicator(summarySortKey, 'salary', summarySortDir)}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="sortable-th"
                    onClick={() => toggleSummarySort('expectedRemainingCap')}
                  >
                    Expected Remaining Cap{sortIndicator(summarySortKey, 'expectedRemainingCap', summarySortDir)}
                  </button>
                </th>
                <th>F</th>
                <th>D</th>
                <th>G</th>
              </tr>
            </thead>
            <tbody>
              {teamSummary.map((row) => {
                const isSelected = teamFilter === row.team_key;

                return (
                  <tr
                    key={row.team_key}
                    className={isSelected ? 'pending-fa-summary-selected' : 'pending-fa-summary-row'}
                    onClick={() => handleTeamRowClick(row.team_key)}
                  >
                    <td className="name-td pending-fa-team-cell">
                      <img
                        src={row.team_logo_url}
                        alt={row.team_name}
                        className="team-logo"
                      />
                      <span>{row.team_name}</span>
                    </td>
                    <td>{row.players}</td>
                    <td>{row.salary}</td>
                    <td>{row.expectedRemainingCap}</td>
                    <td>{row.forwards}</td>
                    <td>{row.defense}</td>
                    <td>{row.goalies}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>

        <div>
          <Table responsive bordered hover className="table pending-fa-player-table">
            <thead>
              <tr>
                <th className="name-th">Player</th>
                <th>
                  <button
                    type="button"
                    className="sortable-th"
                    onClick={() => toggleSort('salary')}
                  >
                    Salary{sortIndicator(sortKey, 'salary', sortDir)}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="sortable-th"
                    onClick={() => toggleSort('market_value')}
                  >
                    Market Value{sortIndicator(sortKey, 'market_value', sortDir)}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="sortable-th"
                    onClick={() => toggleSort('team')}
                  >
                    Team{sortIndicator(sortKey, 'team', sortDir)}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedPlayers.map((player_contract) => (
                <ContractListPlayerRow
                  key={player_contract.player_key}
                  player={player_contract}
                />
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default PendingFAList;
